import clsx from 'clsx';
import _, { isEqual } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import type { FormState, SubmitHandler } from 'react-hook-form';
import { useSelector } from 'react-redux';

import ClickAwayListener from '@/src/components/common/ClickAwayListener/ClickAwayListener';
import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import SectionLoader, {
  LOADER_POSITION,
  LOADER_TYPE,
} from '@/src/components/common/Elements/Spinner/SectionLoader/SectionLoader';
import { showToast } from '@/src/globalFunction';
import useNetworkState from '@/src/hooks/useNetworkState';
import { queryClient } from '@/src/lib/react-query/react-query';
import { RootState } from '@/src/reducers/types';
import { parseNumber } from '@/src/util/numbers';

import { BaseFormOption } from './BaseForm/BaseForm';
import Computations from './Computations/Computations';
import TextAreaField from './Fields/TextAreaField/TextAreaField';
import LineItemsFormModal from './LineItems/LineItemsFormModal/LineItemsFormModal';
import TreatmentFormLayout from './TreatmentFormLayout/TreatmentFormLayout';
import useTreatmentCalculator from './useTreatmentCalculator';
import useTreatmentConfirmModals from './useTreatmentConfirmModals';

import { useSaveDraft } from '../../api/createDraft';
import { useSaveTreatment } from '../../api/createTreatment';
import { useDeleteDraft } from '../../api/deleteDraft';
import { GetDraftsResponse, treatmentDraftsQueryKey } from '../../api/getDrafts';
import useGetLinesSuggestions from '../../api/getLineSuggestions';
import { useTreatmentOptions } from '../../api/getRemoteOptions';
import { quickbooksTreatmentsKeys } from '../../api/getTreatments';
import { useUpdateDraft } from '../../api/updateDraft';
import { MESSAGE_ERRORS, QB_ERRORS, DEFAULT_PUBLISH_ERROR, DEFAULT_CURRENCY } from '../../Config';
import ActionButton from '../../Elements/ActionButton/ActionButton';
import {
  findCurrencyFromCurrencies,
  mapDraftLineItems,
  mapDraftValueToTreatmentForm,
  mapFormData,
  mapLineItems,
  mapLineItemsAndAccountsPublishData,
  mapTreatments,
} from '../../Helper';
import { TaxCalculationEnum } from '../../models/Treatment/TreatmentCalculator';
import { TreatmentZustandStore } from '../../store/treatments.store';
import { RemoteOptions, CalculationStrategy } from '../../types';
import {
  AutomatedDraft,
  AutomatedDraftForm,
  AutomatedDraftPayload,
  Draft,
} from '../../types/draft';
import {
  LineEntryForm,
  TreatmentForm,
  FormTypeOption,
  Treatment,
  UnpaidBillTreatmentForm,
  CheckPaymentTreatmentForm,
  PaidBillTreatmentForm,
} from '../../types/treatment';
import AutomationBanner from '../Automation/AutomationBanner';
import { FORM_TYPES } from '../CreateTreatment/CreateTreatment';
import LineItems from '../LineItems/LineItems';

const DEFAULT_REMOTE_OPTIONS: RemoteOptions = {
  lineItems: {},
  defaultMemo: null,
  defaultCurrency: null,
  payees: [],
  bankAccounts: [],
  terms: [],
  locations: [],
  paymentAccounts: [],
  paymentMethods: [],
};

const DEFAULT_TREATMENT_VALUES: TreatmentForm = {
  referenceNumber: null,
  transactionDate: null,
  location: null,
  taxCalculation: null,
  memo: '',
  currency: DEFAULT_CURRENCY,
  totalAmount: 0,
  lineItems: null,
  lineAccounts: null,
};

const checkAtLeastOneFieldFilled = (fields: TreatmentForm) => {
  for (const fieldKey of Object.keys(fields)) {
    const field = fields[fieldKey];
    if (!field) {
      continue;
    }

    // Exemptions
    if (fieldKey === 'taxCalculation') {
      continue;
    }

    if (fieldKey === 'currency') {
      continue;
    }

    if (fieldKey === 'memo') {
      continue;
    }

    if (typeof field === 'object' && field.value) {
      return true;
    }

    if (typeof field === 'string' && field) {
      return true;
    }
  }
  return false;
};

const getSubmitTooltip = (isSubmittable: boolean) => {
  return !isSubmittable
    ? {
        place: 'left',
        effect: 'solid',
        content: 'Complete the mandatory fields to publish',
      }
    : null;
};

const checkSubmittable = ({
  form,
  isValid,
  lineItems,
  isTaxRequired,
}: {
  form: FormTypeOption;
  isValid: FormState<TreatmentForm>['isValid'];
  lineItems: LineEntryForm[];
  isTaxRequired: boolean;
}) => {
  const isFormSubmittable = !!isValid && form.id !== 'Base';

  return (
    isFormSubmittable &&
    lineItems.filter((li) => {
      return (
        (!!li.account || !!li.item) &&
        !!li.qty &&
        !!li.unitPrice &&
        !!li.totalAmount &&
        (isTaxRequired ? !!li.taxCode : true)
      );
    }).length > 0
  );
};

const TreatmentForm = ({
  form,
  draft,
  documentId,
  onBack,
  onPublished,
  onDraftAction,
  setTreatment,
  setForm,
  onUpdateDraft,
  removeFromAutomation,
}: {
  form: FormTypeOption;
  draft: Draft | AutomatedDraft;
  documentId: number;
  onBack: () => void;
  onDraftAction: () => void;
  onPublished: (isShowSuccessToast: boolean) => void;
  setTreatment: (treatment: Treatment) => void;
  setForm: (form: FormTypeOption) => void;
  onUpdateDraft: (draft: Draft | AutomatedDraft) => void;
  removeFromAutomation: TreatmentZustandStore['removeFromAutomation'];
}) => {
  const methods = useForm<TreatmentForm | AutomatedDraftForm>({
    mode: 'onBlur',
    defaultValues: DEFAULT_TREATMENT_VALUES,
  });
  const {
    register,
    control,
    handleSubmit: formHandleSubmit,
    formState,
    reset,
    resetField,
    watch,
    setValue,
    getValues,
    trigger,
  } = methods;

  const { isValid, defaultValues, dirtyFields } = formState;
  const { data: loggedUserData } = useSelector((store: RootState) => store.loggedUser);
  const {
    Selected: { isLineSuggestionsEnabled },
  } = useSelector((store: RootState) => store.project);

  const [draftId, setDraftId] = useState<number | null>(draft?.draftId || null);
  const [tempDraft, setTempDraft] = useState<TreatmentForm>(null);
  const [isDraftPrefilled, setIsDraftPrefilled] = useState<boolean>(false);

  const [lineItems, setLineItems] = useState<LineEntryForm[]>([]);
  const [taxCalculation, setTaxCalculation] = useState<TreatmentForm['taxCalculation'] | null>(
    null
  );
  const watchedFields = watch();
  const payeeOrVendor =
    (watchedFields as UnpaidBillTreatmentForm).vendor ||
    (watchedFields as PaidBillTreatmentForm | CheckPaymentTreatmentForm).payee;

  const isTaxRequired = taxCalculation?.value !== TaxCalculationEnum.NotApplicable;

  const [lineAccounts, setLineAccounts] = useState<any[]>([]);
  // Popup Modals
  const { ConfirmSuccessModal, confirmSuccess } = useTreatmentConfirmModals();
  const { ConfirmErrorModal, confirmError, setErrorPopup } = useTreatmentConfirmModals();
  const { ConfirmDeleteDraftModal, confirmDeleteDraft } = useTreatmentConfirmModals();
  const { ConfirmClearDataModal, confirmClearData } = useTreatmentConfirmModals();
  const { DiscardChangesModal, confirmDiscard } = useTreatmentConfirmModals();
  const { NetworkErrorModal, confirmNetworkError } = useTreatmentConfirmModals();

  const isAtLeastOneFieldFilled =
    checkAtLeastOneFieldFilled(watchedFields) ||
    lineItems.length > 0 ||
    form?.id !== FORM_TYPES.Base.id;
  const networkState = useNetworkState();
  const [remoteOptions, setRemoteOptions] = useState<RemoteOptions | typeof DEFAULT_REMOTE_OPTIONS>(
    DEFAULT_REMOTE_OPTIONS
  );
  const remoteOptionsQuery = useTreatmentOptions({
    fileId: documentId,
  });

  const saveTreatmentMutation = useSaveTreatment();
  const saveDraftMutation = useSaveDraft();
  const updateDraftMutation = useUpdateDraft();
  const deleteDraftMutation = useDeleteDraft();

  const isFormLoading =
    saveTreatmentMutation.isLoading ||
    saveDraftMutation.isLoading ||
    updateDraftMutation.isLoading ||
    deleteDraftMutation.isLoading;
  const isRemoteOptionsLoading = remoteOptionsQuery.isLoading && remoteOptionsQuery.isFetching; // No skeleton loading
  const isSubmittable =
    checkSubmittable({ form, isValid, lineItems, isTaxRequired }) && !isFormLoading;
  const { totals, treatmentCalculationStrategy } = useTreatmentCalculator({
    lineItems,
    taxCalculation: taxCalculation,
  });
  const BASE_FORM_TYPE = BaseFormOption;

  // If we need client side for file size validation. We can extract the network error and file size error here
  // const checkClientSideErorors = useCallback(async ({ response, customError }) => {
  //     let error = null;

  //     if (customError) {
  //         error = customError;
  //     }

  //     if(response && response.data.data.code) {
  //         error = QB_ERRORS[response.data.data.code];
  //     }

  //     if (!(await confirmError(error))) {
  //         return;
  //     }
  // }, [])

  const handleConfirmSuccess = useCallback(
    async (url) => {
      if (!(await confirmSuccess())) {
        return;
      }

      window.open(url, '_blank');

      return Promise.resolve();
    },
    [confirmSuccess]
  );

  const handlePromptError = useCallback(
    async ({ response = null, customError = null }) => {
      let error = null;

      if (customError) {
        error = customError;
      } else if (response && response.data && response.data.data && response.data.data.code) {
        error = QB_ERRORS[response.data.data.code] || {
          title: DEFAULT_PUBLISH_ERROR.title,
          message: `${response.data.data.element && `${response.data.data.element}:`}
          ${response.data.data.Detail} [Error Code: ${response.data.data.code}]`,
        };
      } else if (response && response.data && response.data.message) {
        error = {
          title: DEFAULT_PUBLISH_ERROR.title,
          message: response.data.message,
        };
      }
      setErrorPopup(error);
      if (!(await confirmError())) {
        return;
      }
    },
    [confirmError, setErrorPopup]
  );

  const updatedIsAutomatedStatus = useCallback(
    (formData: TreatmentForm | AutomatedDraftForm) => {
      if (!draft || !draft.automationId) return [];
      const automatedFields = {};
      Object.keys(dirtyFields).map((dirtyField) => {
        const automatedField = `${dirtyField}IsAutomated` as keyof AutomatedDraftPayload;
        if (formData[automatedField]) {
          automatedFields[automatedField] = false;
        }
      });

      if (
        (draft as AutomatedDraft).payload.taxCalculationIsAutomated &&
        !isEqual(taxCalculation?.publishData, draft.payload.taxCalculation)
      ) {
        automatedFields['taxCalculationIsAutomated'] = false;
      }

      return automatedFields;
    },
    [dirtyFields, draft, taxCalculation?.publishData]
  );

  const prepareFormDataForSubmit = (
    data: TreatmentForm | AutomatedDraftForm,
    isPublish: boolean = false
  ): Treatment['payload'] | AutomatedDraft['payload'] => {
    const formData: Treatment['payload'] | AutomatedDraftPayload = {
      ...mapFormData(data),
      ...mapLineItemsAndAccountsPublishData(
        lineItems,
        treatmentCalculationStrategy as CalculationStrategy,
        isPublish
      ),
      totalAmount: parseNumber(totals.total),
      currency: data.currency,
      draftId,
      detectedVendor: (data as AutomatedDraftForm).detectedVendor,
    };

    if (!(data as AutomatedDraftForm)?.detectedVendor) {
      delete (formData as AutomatedDraftPayload).detectedVendor;
    }

    // remove unnecessary fields by treatment type
    if (form.id === 'Bill') {
      const vendor = formData.payee; // use vendor instead of payee
      delete formData.payee;
      delete formData.paymentAccount;
      delete formData.paymentMethod;
      delete formData.bankAccount;
      formData.vendor = vendor;
    } else if (form.id === 'Check') {
      delete formData.paymentAccount;
      delete formData.paymentMethod;
      delete formData.salesTerm;
      delete formData.dueDate;
      delete formData.vendor;
    } else {
      delete formData.bankAccount;
      delete formData.salesTerm;
      delete formData.dueDate;
      delete formData.vendor;
    }

    return formData;
  };

  const handleSubmit: SubmitHandler<TreatmentForm> = async (data) => {
    if (!networkState.online) {
      confirmNetworkError();
      return;
    }
    const updatedAutomationStatus = updatedIsAutomatedStatus(getValues());
    const formData = prepareFormDataForSubmit({ ...data, ...updatedAutomationStatus }, true);
    const response = await saveTreatmentMutation.mutateAsync({
      documentId,
      params: formData,
      saveTreatmentEndpoint: form.saveTreatmentEndpoint,
    });

    if (response && response.transactionId) {
      const isShowSuccessToast = !form.customSuccessModal;

      if (form.customSuccessModal) {
        await handleConfirmSuccess(response.url);
      }

      if (onPublished) {
        onPublished(isShowSuccessToast);
      }

      if (draft) {
        removeFromAutomation(documentId);

        queryClient.setQueryData(
          [...treatmentDraftsQueryKey(documentId)],
          (prev: GetDraftsResponse) => {
            return prev.filter((prevDraft) => prevDraft.draftId !== draft.draftId);
          }
        );
      }

      setTreatment(
        mapTreatments({
          type: form.id,
          payload: JSON.stringify(formData),
          transactionId: response.transactionId,
          documentId: documentId,
          url: response.url,
        }) as Treatment
      );

      queryClient.setQueryData([...quickbooksTreatmentsKeys.detail(documentId)], (prev: any) => {
        return {
          ...prev,
          transactions: [
            ...prev.transactions,
            {
              transactionId: response.transactionId,
              documentId,
              url: response.url,
              type: form.id,
              payload: JSON.stringify(formData),
              transactionDate: response.transactionDate,
            },
          ],
        };
      });
    } else {
      handlePromptError({ response });
    }
  };

  const handleSaveDraft = async () => {
    if (!networkState.online) {
      confirmNetworkError();
      return;
    }

    const updatedAutomationStatus = updatedIsAutomatedStatus(getValues());
    const formData = { ...getValues(), ...updatedAutomationStatus };
    const transaction = prepareFormDataForSubmit(formData);

    const formType = form.id !== 'Base' ? form.id : null;
    const params = { transaction, type: formType };

    let response = null;
    if (draftId) {
      response = await updateDraftMutation.mutateAsync({
        documentId,
        draftId,
        params,
      });
    } else {
      response = await saveDraftMutation.mutateAsync({
        documentId,
        params,
      });
    }

    if (response && response.draftId) {
      setDraftId(response.draftId);
      reset(formData);
      setTempDraft(formData);
      removeFromAutomation(documentId);
      onUpdateDraft({
        ...draft,
        type: formType,
        payload: transaction as AutomatedDraft['payload'],
      });
      showToast('success', 'Changes have been saved.');

      queryClient.setQueryData(
        [...treatmentDraftsQueryKey(documentId)],
        (prev: GetDraftsResponse) => {
          const draftExists = prev.findIndex((d) => d.draftId === response.draftId);

          return draftExists >= 0
            ? prev.map((draft) => {
                if (draft.draftId === response.draftId) {
                  return {
                    ...draft,
                    type: form.id,
                    transaction,
                    modifiedBy: loggedUserData.id,
                  };
                }
                return draft;
              })
            : [
                {
                  draftId: response.draftId,
                  documentId,
                  type: form.id,
                  transaction,
                  modifiedBy: loggedUserData.id,
                },
                ...prev,
              ];
        }
      );
      onDraftAction();
    } else {
      handlePromptError({ response });
    }
  };

  const handleReset = useCallback(async () => {
    if (!(await confirmClearData())) return;

    setLineItems([]);

    const defaultTaxCalculation = remoteOptions.lineItems.taxCalculation[0];
    setTaxCalculation(defaultTaxCalculation);

    // re-apply default values
    reset({
      ...DEFAULT_TREATMENT_VALUES,
      memo: remoteOptions.defaultMemo,
      taxCalculation: defaultTaxCalculation,
      currency: remoteOptions.defaultCurrency,
    });
    setForm(BASE_FORM_TYPE);
    // onUpdateDraft(null);

    showToast('success', 'Successfully cleared fields!');
  }, [
    BASE_FORM_TYPE,
    remoteOptions.lineItems.taxCalculation,
    remoteOptions.defaultMemo,
    remoteOptions.defaultCurrency,
    reset,
    setForm,
    confirmClearData,
  ]);

  const handlePromptDiscardAndBack = useCallback(async () => {
    if (isAtLeastOneFieldFilled || tempDraft) {
      if (tempDraft) {
        const formData = getValues();
        const isChanged = !_.isEqual(tempDraft, formData);

        if (!isChanged) {
          return true;
        }
      }

      if (!(await confirmDiscard())) return;

      reset(DEFAULT_TREATMENT_VALUES);
      setForm(null);
      onBack();

      return false;
    }

    return true;
  }, [isAtLeastOneFieldFilled, tempDraft, confirmDiscard, reset, setForm, onBack, getValues]);

  const handleBack = useCallback(async () => {
    if (await handlePromptDiscardAndBack()) {
      // Can go back if no changes
      onBack();
    }
  }, [handlePromptDiscardAndBack, onBack]);

  const handleClickAway = useCallback(async () => {
    await handlePromptDiscardAndBack();
  }, [handlePromptDiscardAndBack]);

  const handleDeleteDraft = useCallback(async () => {
    if (!(await confirmDeleteDraft())) return;

    const response = await deleteDraftMutation.mutateAsync({
      draftId,
      documentId,
    });

    if (response && response.draftId) {
      showToast('success', 'Successfully deleted the draft');

      queryClient.setQueryData([...treatmentDraftsQueryKey(documentId)], (prev: any) => {
        return prev.filter((draft) => draft.draftId !== draftId);
      });
      onDraftAction();
      onBack();
    } else {
      handlePromptError({ response });
    }
  }, [
    confirmDeleteDraft,
    deleteDraftMutation,
    draftId,
    documentId,
    onDraftAction,
    onBack,
    handlePromptError,
  ]);

  // Recalculate line item prices
  // useEffect(() => {
  //     if (!taxCalculation || !remoteOptions) {
  //         return;
  //     }

  //     setLineItems(lineItems.map(lineItem => {
  //         const unitPrice = calculateTaxedAmountFromItemSetting({
  //             // @TODO: consider taxIncluded if item setting is activated and confirmed
  //             item: {
  //                 price: lineItem.unitPrice,
  //                 taxCode: lineItem.taxCode,
  //             },
  //             dropdowns: remoteOptions.lineItems.taxCodes,
  //             taxCalculation
  //         });
  //         // const amount = calculateAmount(lineItem.qty || 1, unitPrice);

  //         return {
  //             ...lineItem,
  //             unitPrice: unitPrice,
  //             amount: unitPrice,
  //         }
  //     }));
  // }, [taxCalculation, remoteOptions])

  // apply default values on fresh form
  useEffect(() => {
    if (
      _.isEqual(remoteOptions, DEFAULT_REMOTE_OPTIONS) ||
      !_.isEqual(defaultValues, DEFAULT_TREATMENT_VALUES) ||
      !_.isEmpty(draft)
    ) {
      return;
    }

    setTaxCalculation(remoteOptions.lineItems.taxCalculation[0]);
    reset({
      ...DEFAULT_TREATMENT_VALUES,
      memo: remoteOptions.defaultMemo,
      currency: remoteOptions.defaultCurrency,
    });
  }, [remoteOptions, draft, defaultValues, reset]);

  useEffect(() => {
    if (!remoteOptionsQuery.data) {
      return;
    }

    const newRemoteOptions: RemoteOptions = form.mapRemoteOptions(remoteOptionsQuery.data);
    setRemoteOptions(newRemoteOptions);

    // reset payee if payee is not on remoteOptions of the selected form
    const payee = getValues('payee');
    if (payee) {
      const foundPayee = newRemoteOptions.payees.find((payees) => payees.value === payee.value);
      if (!foundPayee) setValue('payee', null);
    }
  }, [form, remoteOptionsQuery.data, setValue, getValues]);

  // if form type changes, reset the disabled fields
  useEffect(() => {
    if (form.id === 'Base') return;
    const { isMethodDisabled, isAccountDisabled, isTermsDisabled, isDueOnDisabled } =
      form.disabledFields;

    if (isAccountDisabled) {
      resetField('paymentAccount');
      resetField('bankAccount');
    }
    if (isMethodDisabled) resetField('paymentMethod');
    if (isTermsDisabled) resetField('salesTerm');
    if (isDueOnDisabled) resetField('dueDate');
  }, [form.id, form.disabledFields, resetField]);

  useEffect(() => {
    if (isDraftPrefilled || !draft || _.isEqual(remoteOptions, DEFAULT_REMOTE_OPTIONS)) return;
    const draftFormValues = mapDraftValueToTreatmentForm(draft, remoteOptions);
    const draftLineItems = mapDraftLineItems(
      [...draft.payload.lineAccounts, ...draft.payload.lineItems],
      remoteOptions['lineItems']
    );

    reset(draftFormValues);
    setTempDraft(draftFormValues);

    setLineItems(draftLineItems);
    setTaxCalculation(draftFormValues.taxCalculation);
    setIsDraftPrefilled(true);
  }, [draft, remoteOptions, reset, isDraftPrefilled, setValue]);

  useEffect(() => {
    if (!payeeOrVendor || !remoteOptions.currencies) return;

    const currency = findCurrencyFromCurrencies(
      remoteOptions.currencies,
      payeeOrVendor?.currency?.value
    );

    if (currency) {
      setValue('currency', currency, { shouldDirty: false });
    }
  }, [payeeOrVendor, remoteOptions?.currencies, setValue]);

  const payeeData = useMemo(
    () => ({
      payeeId: payeeOrVendor?.value || '',
      payeeType:
        payeeOrVendor?.type === 'Payee'
          ? 'Employee'
          : payeeOrVendor?.type === 'Supplier'
            ? 'Vendor'
            : 'Vendor',
    }),
    [payeeOrVendor]
  );

  const { data: lineAccountss, isLoading, error } = useGetLinesSuggestions(documentId, payeeData);

  useEffect(() => {
    if (draft) return;
    if (Array.isArray(lineAccountss)) {
      // Ignore account and class type from the getLineSuggestions API
      setLineAccounts(
        lineAccountss.map((entry) => ({
          ...entry,
          account: entry.account ? (({ type, ...rest }) => rest)(entry.account) : entry.account,
          class: entry.class ? (({ type, ...rest }) => rest)(entry.class) : entry.class,
        }))
      );
    }
  }, [lineAccountss, draft]);

  useEffect(() => {
    if (draft) return;
    if (Array.isArray(lineAccounts) && lineAccounts.length > 0) {
      const prepopulatedLineItems = lineAccounts?.map((suggestion) => ({
        account: suggestion.account || '',
        description: suggestion.description || '',
        qty: suggestion.qty || 0,
        unitPrice: suggestion.unitPrice || 0,
        totalAmount: suggestion.totalAmount || 0,
        taxCode: suggestion.taxCode || null,
        item: suggestion.item || null,
        class: suggestion.class || null,
      }));
      const tempLineItems = mapLineItems(prepopulatedLineItems, remoteOptions.lineItems);

      setLineItems(tempLineItems);
    }
  }, [lineAccounts, remoteOptions.lineItems, setLineItems, draft]);

  useEffect(() => {
    setValue('taxCalculation', taxCalculation);
  }, [setValue, taxCalculation]);

  useEffect(() => {
    if (!saveTreatmentMutation.error) return;
    handlePromptError({
      response: (saveTreatmentMutation.error as { response?: any }).response,
    });
  }, [handlePromptError, saveTreatmentMutation.error]);

  const isShowAutomationBanner = useMemo(() => {
    if (!draft || !draft?.automationId) return false;

    // get all `IsAutomated` values that are true
    const automatedFields = Object.entries(draft.payload).filter(
      ([fieldKey, fieldValue]) =>
        !!fieldValue &&
        !_.isUndefined(draft.payload[`${fieldKey}IsAutomated`]) &&
        draft.payload[`${fieldKey}IsAutomated`] === true
    );

    const lineEntries = [...draft.payload.lineAccounts, ...draft.payload.lineItems];
    const automatedLineEntries = lineEntries.filter((lineEntry) => {
      return (
        Object.entries(lineEntry).filter(
          ([fieldKey, fieldValue]) =>
            !!fieldValue &&
            !_.isUndefined(lineEntry[`${fieldKey}IsAutomated`]) &&
            lineEntry[`${fieldKey}IsAutomated`] === true
        ).length > 0
      );
    });

    return (
      automatedFields.length > 0 ||
      automatedLineEntries.length > 0 ||
      (draft as AutomatedDraft).payload?.detectedVendor
    );
  }, [draft]);

  const isShowManualExpenseFormBanner = useMemo(() => {
    return Array.isArray(lineAccounts) && lineAccounts.length > 0;
  }, [lineAccounts]);

  const banner = useMemo(
    () =>
      (isShowAutomationBanner || isShowManualExpenseFormBanner) && (
        <AutomationBanner
          isShowNewExpenseForm={isShowManualExpenseFormBanner}
          isShowAutomationBanner={!!isShowAutomationBanner}
        >
          <Icon
            icon={ICONS.automation_on({
              strokeClasses: isShowManualExpenseFormBanner
                ? 'tw-stroke-cfo-blue-600 tw-fill-cfo-blue-600'
                : 'tw-stroke-cfo-purple-200 tw-fill-cfo-purple-200',
              width: '16px',
              height: '16px',
            })}
            className={clsx('tw-text-[15px] tw-mr-[5px]')}
          />
          {isShowManualExpenseFormBanner
            ? `Draft has been pre-filled based on last entry for the selected supplier`
            : `We've pre-identified some information to get you started. Please review before
          publishing.`}
        </AutomationBanner>
      ),
    [isShowAutomationBanner, isShowManualExpenseFormBanner]
  );

  /* Section loader upon initial load of treatment panel */
  if (isRemoteOptionsLoading) {
    return (
      <div
        className={clsx(
          'tw-flex tw-items-center tw-justify-center',
          'tw-text-center',
          'tw-min-h-[calc(100vh-340px)]'
        )}
      >
        <SectionLoader loaderType={LOADER_TYPE.screen} />
      </div>
    );
  }

  const footer = (
    <React.Fragment>
      <div
        className={clsx(
          'treatmentFormFooter--container',
          'tw-flex tw-items-center tw-justify-between'
        )}
      >
        <ActionButton
          className={clsx(
            'tw-max-w-[80px] tw-w-full tw-h-[32px]',
            'tw-p-[5px]',
            isAtLeastOneFieldFilled ? 'tw-text-cfo-black' : 'tw-text-cfo-grey-600',
            isAtLeastOneFieldFilled &&
              'hover:tw-text-white hover:tw-border-cfo-blue-700 hover:tw-bg-cfo-blue-700'
          )}
          buttonType={'filled_inverse'}
          onClick={handleReset}
          disabled={!isAtLeastOneFieldFilled || isFormLoading}
        >
          Clear
        </ActionButton>

        <div className={clsx('tw-flex tw-items-center')}>
          <ActionButton
            id="saveDraft--btn"
            className={clsx('tw-min-w-[120px] tw-w-full tw-h-[32px]', 'tw-p-[5px]')}
            buttonType={'filled_inverse'}
            type="button"
            data-submittype="draft"
            onClick={handleSaveDraft}
            disabled={isFormLoading}
          >
            Save as Draft
          </ActionButton>

          <ActionButton
            id="transaction__btn-submit"
            className={clsx(
              'tw-min-w-[150px] tw-w-full tw-h-[32px]',
              'tw-rounded-[5px]',
              'tw-p-[5px]',
              isSubmittable && 'active !tw-text-white !tw-border-cfo-blue-700 !tw-bg-cfo-blue-700'
            )}
            buttonType={'filled_inverse'}
            type="submit"
            disabled={!isSubmittable}
            tooltip={getSubmitTooltip(isSubmittable)}
            data-submittype="publish"
          >
            Save & Publish
          </ActionButton>
        </div>
      </div>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <form
          className={clsx('tw-relative tw-h-full tw-overflow-auto')}
          style={{ maxWidth: '100%' }}
          onSubmit={formHandleSubmit(handleSubmit)}
        >
          {/* Section loader upon creating treatment */}
          {isFormLoading && (
            <SectionLoader
              className={clsx(
                'tw-flex tw-items-center tw-justify-center',
                'tw-text-center',
                'tw-min-h-[calc(100vh-340px)]'
              )}
              loaderType={LOADER_TYPE.screen}
              loaderPosition={LOADER_POSITION.absolute}
            />
          )}

          <ClickAwayListener onClickAway={handleClickAway}>
            <React.Fragment>
              <TreatmentFormLayout
                isDraft={!!draftId}
                footer={footer}
                banner={banner}
                isViewMode={false}
                onBack={handleBack}
                onDeleteDraft={handleDeleteDraft}
                isFormLoading={isFormLoading}
              >
                <form.Component register={register} control={control} dropdowns={remoteOptions} />
                <LineItems
                  isDisplayView={false}
                  lineItems={lineItems}
                  isPrefilled={lineAccounts?.length > 0}
                >
                  <LineItemsFormModal
                    lineItems={lineItems}
                    taxCalculation={taxCalculation}
                    currency={watchedFields.currency}
                    setLineItems={setLineItems}
                    setTaxCalculation={setTaxCalculation}
                    dropdowns={remoteOptions.lineItems}
                    isAutomated={!!draft && !!draft.automationId}
                    taxCalculationIsAutomated={
                      (!!draft && (draft.payload as AutomatedDraft['payload']))
                        ?.taxCalculationIsAutomated
                    }
                    draftLineEntries={
                      draft ? [...draft.payload.lineAccounts, ...draft.payload.lineItems] : []
                    }
                    draftTaxCalculation={
                      (!!draft && (draft.payload as AutomatedDraft['payload']))?.taxCalculation
                    }
                    isPrefilled={lineAccounts?.length > 0}
                    onClose={() => {
                      trigger();
                    }}
                  />
                </LineItems>

                <div className={clsx('tw-flex tw-items-end tw-justify-between', 'tw-mt-[10px]')}>
                  <p className={clsx('tw-mb-[25px]', 'tw-flex tw-items-center')}>
                    <span className={clsx('tw-text-cfo-grey-600 tw-mr-[5px]')}>Amounts are:</span>
                    <span
                      className={clsx('tw-text-cfo-black', {
                        'tw-bg-[rgba(240,232,250,1)]':
                          !!draft &&
                          (draft.payload as AutomatedDraft['payload'])?.taxCalculationIsAutomated &&
                          _.isEqual(
                            taxCalculation?.publishData,
                            (draft.payload as AutomatedDraft['payload'])?.taxCalculation
                          ),
                      })}
                    >
                      {taxCalculation?.label}
                    </span>
                  </p>
                  <Computations
                    totals={totals}
                    currency={watchedFields.currency}
                    className={clsx('tw-pt-[10px] ')}
                  />
                </div>

                <div
                  className={clsx(
                    'tw-border-solid tw-border-t-[1px] tw-border-t-cfo-grey-500',
                    'tw-pt-[10px] tw-mt-[10px]'
                  )}
                >
                  <TextAreaField
                    label="Memo"
                    labelClassName={clsx('tw-text-cfo-grey-600')}
                    placeholder={remoteOptions.defaultMemo}
                    registration={register('memo')}
                    maxLength={1000}
                    isAiGenerated={false}
                  />
                </div>
              </TreatmentFormLayout>

              <DiscardChangesModal />
              <ConfirmSuccessModal />
              <ConfirmErrorModal />
              <ConfirmDeleteDraftModal />
              <ConfirmClearDataModal />
              <NetworkErrorModal />
            </React.Fragment>
          </ClickAwayListener>
        </form>
      </FormProvider>
    </React.Fragment>
  );
};

export default TreatmentForm;
